import React, { useEffect, useState, useCallback, useRef, Component } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { Modal, Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers, faPhone, faUserCheck, faLayerGroup,
  faChartLine, faCalendarWeek, faHourglassHalf, faClock,
  faHeadset, faHandshake, faTasks,
  faExclamationTriangle, faCheckCircle, faTimesCircle, faMinusCircle,
  faSlidersH, faSyncAlt, faPrint, faSearch, faTimes,
  faChevronDown, faChevronUp, faFire, faSnowflake,
  faBookmark, faBullhorn, faFilter, faPauseCircle,
  faPhoneSlash, faUserTimes, faCalendarAlt, faInbox, faSave, faSpinner,
  faFileDownload, faIdCard,
} from '@fortawesome/free-solid-svg-icons'
import Layout from '../../../../app/Layout'
import HeaderContent from '../../General/HeaderContent'
import Breadcrumb from '../../General/Breadcrumb'
import SpinnerLoading from '../../../components/Spinners/SpinnerLoading'
import authAxios from '../../../../config/authAxios'

/* ── helpers ─────────────────────────────────────────────────────────────── */
const fmt    = n => Number(n || 0).toLocaleString('es-PE')
const pct    = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0
const ini    = n => n?.split(' ').filter(Boolean).slice(0, 2).map(x => x[0]?.toUpperCase() || '').join('') || '?'
const mesLbl = ym => {
  if (!ym) return ''
  const [y, m] = ym.split('-')
  const N = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
  return `${N[+m]} ${y}`
}

/* ── colores Bootstrap del sistema ──────────────────────────────────────── */
const C_V = '#28a745'
const C_A = '#fd7e14'
const C_R = '#dc3545'
const C_G = '#6c757d'

const semIns  = (v, m)    => v >= m       ? C_V : v >= m * .8      ? C_A : C_R
const semConv = (v, m=10) => v >= m       ? C_V : v >= m * .8      ? C_A : C_R
const semResp = (v, m=35) => v >= m       ? C_V : v >= m * .7      ? C_A : C_R
const semMad  = (v, m=3)  => v == null    ? C_G : v <= m ? C_V : v <= m + 1 ? C_A : C_R
const semCont = (v, m=10) => v == null    ? C_G : v <= m ? C_V : v <= m * 2 ? C_A : C_R
const semHef  = (v, m=4)  => v >= m       ? C_V : v >= m * .75     ? C_A : C_R
const semTrat = (v, m=3)  => v >= m       ? C_V : v >= Math.max(1, m - 1) ? C_A : C_R
const semProc = (v, m=20) => v <= m * .5  ? C_V : v <= m           ? C_A : C_R
const semSem  = (i, r, mI, mR) => (i >= mI || r >= mR) ? C_V : (i >= mI * .67 || r >= mR * .7) ? C_A : C_R

/* ── Pill semáforo ───────────────────────────────────────────────────────── */
function Pill({ color, children, size = 12 }) {
  const col = color || C_G
  return (
    <span style={{ fontSize: size, fontWeight: 600, color: col, backgroundColor: `${col}18`, padding: '2px 8px', borderRadius: 20 }}>
      {children}
    </span>
  )
}

/* ── Dot semáforo ────────────────────────────────────────────────────────── */
function Dot({ color, size = 10 }) {
  return <span style={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', backgroundColor: color || C_G, flexShrink: 0 }} />
}

/* ── Combobox buscador de programas ──────────────────────────────────────── */
function ProgramCombobox({ proyectos, pidSel, onChange }) {
  const [open,   setOpen]   = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef(null)

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const selected = proyectos.find(p => p.id_proyecto === pidSel)
  const filtered = proyectos.filter(p =>
    p.nombre_proyecto.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 280 }}>
      <div
        onClick={() => setOpen(o => !o)}
        className="d-flex align-items-center"
        style={{
          border: `1px solid ${open ? '#007bff' : '#ced4da'}`,
          borderRadius: 4, padding: '6px 10px', background: '#fff', cursor: 'pointer',
          boxShadow: open ? '0 0 0 2px rgba(0,123,255,.25)' : 'none',
        }}
      >
        <FontAwesomeIcon icon={faLayerGroup} className="mr-2 text-muted" style={{ fontSize: 13 }} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: selected ? '#212529' : '#6c757d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selected ? selected.nombre_proyecto : 'Seleccionar programa…'}
        </span>
        {selected && (
          <span className="badge badge-primary ml-2" style={{ fontSize: 10, flexShrink: 0 }}>
            {proyectos.indexOf(selected) + 1}/{proyectos.length}
          </span>
        )}
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="ml-2 text-muted" style={{ fontSize: 10, flexShrink: 0 }} />
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: '1px solid #dee2e6', borderRadius: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,.15)', overflow: 'hidden',
        }}>
          <div className="d-flex align-items-center px-2 py-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
            <FontAwesomeIcon icon={faSearch} className="text-muted mr-2" style={{ fontSize: 12 }} />
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar programa…"
              style={{ border: 'none', outline: 'none', flex: 1, fontSize: 13, color: '#212529' }}
              onClick={e => e.stopPropagation()}
            />
            {search && (
              <button onClick={e => { e.stopPropagation(); setSearch('') }}
                style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: '#6c757d' }}>
                <FontAwesomeIcon icon={faTimes} style={{ fontSize: 12 }} />
              </button>
            )}
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.length === 0
              ? <p className="text-muted text-center py-3 mb-0" style={{ fontSize: 13 }}>Sin resultados</p>
              : filtered.map(p => (
                <div key={p.id_proyecto}
                  onClick={() => { onChange(p.id_proyecto); setOpen(false); setSearch('') }}
                  className="d-flex align-items-center px-3 py-2"
                  style={{
                    cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f8f9fa',
                    background: p.id_proyecto === pidSel ? '#e8f4fd' : 'transparent',
                    color: p.id_proyecto === pidSel ? '#0069d9' : '#212529',
                    fontWeight: p.id_proyecto === pidSel ? 600 : 400,
                  }}
                  onMouseEnter={e => { if (p.id_proyecto !== pidSel) e.currentTarget.style.background = '#f8f9fa' }}
                  onMouseLeave={e => { if (p.id_proyecto !== pidSel) e.currentTarget.style.background = 'transparent' }}
                >
                  <FontAwesomeIcon
                    icon={p.id_proyecto === pidSel ? faCheckCircle : faMinusCircle}
                    className="mr-2"
                    style={{ fontSize: 11, color: p.id_proyecto === pidSel ? '#0069d9' : '#dee2e6' }}
                  />
                  <span style={{ flex: 1 }}>{p.nombre_proyecto}</span>
                  {p.camp_inicio && <small className="text-muted ml-2">{p.camp_inicio.slice(0, 7)}</small>}
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  )
}

/* ── KPI card (patrón idéntico a ReporteKpis.js) ────────────────────────── */
function KpiCard({ icon, label, value, meta, unit = '', color, accion, progress, large }) {
  const col = color || C_G
  const bw  = progress != null ? Math.min(100, Math.max(0, progress)) : null
  const sz  = large ? { icon: 22, iconBox: 50, value: 38, label: 13 } : { icon: 16, iconBox: 38, value: 24, label: 12 }
  return (
    <div className="box p-3 h-100" style={{ borderTop: `4px solid ${col}` }}>
      <div className="d-flex align-items-start justify-content-between mb-2">
        <div style={{ width: sz.iconBox, height: sz.iconBox, borderRadius: 8, backgroundColor: `${col}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FontAwesomeIcon icon={icon} style={{ color: col, fontSize: sz.icon }} />
        </div>
        <FontAwesomeIcon
          icon={col === C_V ? faCheckCircle : col === C_R ? faTimesCircle : col === C_A ? faExclamationTriangle : faMinusCircle}
          style={{ color: col, fontSize: sz.icon }}
        />
      </div>
      <h2 style={{ color: col, fontWeight: 700, fontSize: sz.value, marginBottom: 2, lineHeight: 1 }}>{value}</h2>
      <p className="mb-1" style={{ fontSize: sz.label, fontWeight: 600, lineHeight: 1.3, color: '#444' }}>{label}</p>
      {bw != null && (
        <div style={{ height: large ? 6 : 4, backgroundColor: '#eee', borderRadius: 2, marginBottom: 4 }}>
          <div style={{ height: large ? 6 : 4, width: `${bw}%`, backgroundColor: col, borderRadius: 2, transition: 'width .4s' }} />
        </div>
      )}
      {meta != null && <small className="text-muted d-block" style={{ fontSize: 11 }}>Meta: {meta}{unit}</small>}
      {accion && <small style={{ fontSize: 11, color: col }}>{accion}</small>}
    </div>
  )
}

/* ── Cierre mensual expandible (integrado en Ranking) ───────────────────── */
function CierreExpandible({ cierres, meses }) {
  const [open, setOpen] = useState(false)
  if (!cierres?.length) return null
  return (
    <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
      <button className="btn btn-outline-secondary btn-sm" onClick={() => setOpen(o => !o)}>
        <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
        Cierre mensual por asesor
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="ml-2" style={{ fontSize: 11 }} />
      </button>
      {open && <div className="mt-3"><ChartCierre cierres={cierres} meses={meses} /></div>}
    </div>
  )
}

/* ── Tabs: Fuentes / Caliente vs Frío / Estados ─────────────────────────── */
function OrigenTabs({ fuentes, tiposLead, estados }) {
  const [tab, setTab] = useState('fuentes')
  const tabs = [
    { k: 'fuentes',  l: 'Por Fuente',        icon: faFilter   },
    { k: 'tipo',     l: 'Caliente vs Frío',   icon: faFire     },
    { k: 'estados',  l: 'Por Estado',         icon: faTasks    },
  ]
  return (
    <div>
      <div className="btn-group btn-group-sm mb-3">
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`btn btn-${tab === t.k ? 'primary' : 'outline-secondary'}`}
            style={{ fontSize: 12 }}>
            <FontAwesomeIcon icon={t.icon} className="mr-1" style={{ fontSize: 11 }} />{t.l}
          </button>
        ))}
      </div>
      {tab === 'fuentes' && (fuentes?.length ? <ChartFuentes fuentes={fuentes} /> : <p className="text-muted text-center py-4 mb-0" style={{ fontSize: 13 }}>Sin datos de fuentes disponibles</p>)}
      {tab === 'tipo' && (
        <>
          <ChartLeadTipos tipos={tiposLead} />
          {tiposLead.length > 1 && (
            <div className="row mt-2">
              {tiposLead.map((t, i) => {
                const isHot = t.tipo === 'Caliente'
                return (
                  <div key={i} className="col-6">
                    <div className="p-2 text-center" style={{ border: `1px solid ${isHot ? '#ffc10740' : '#dee2e6'}`, borderRadius: 4, background: isHot ? '#fff9ee' : '#f8f9fa' }}>
                      <FontAwesomeIcon icon={isHot ? faFire : faSnowflake}
                        style={{ color: isHot ? '#fd7e14' : '#6c757d', fontSize: 16, display: 'block', margin: '0 auto 4px' }} />
                      <div style={{ fontWeight: 700, fontSize: 13, color: isHot ? '#d35400' : '#495057' }}>Lead {t.tipo}</div>
                      <table className="table table-sm table-borderless mb-0 mt-1" style={{ fontSize: 11 }}>
                        <tbody>
                          <tr><td className="text-muted py-0">Leads</td><td className="text-right py-0 font-weight-bold">{fmt(t.leads)}</td></tr>
                          <tr><td className="text-muted py-0">Resp.</td><td className="text-right py-0 font-weight-bold">{t.pct_resp}%</td></tr>
                          <tr><td className="text-muted py-0">Conv.</td><td className="text-right py-0 font-weight-bold">{pct(+t.conv || 0, +t.resp || 0)}%</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
      {tab === 'estados' && (estados?.length ? <ChartProcessing estados={estados} /> : <p className="text-muted text-center py-4 mb-0" style={{ fontSize: 13 }}>Sin datos de procesamiento disponibles</p>)}
    </div>
  )
}

/* ── Error Boundary ──────────────────────────────────────────────────────── */
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(err) { return { error: err } }
  render() {
    if (this.state.error) return (
      <div className="alert alert-danger m-3" style={{ fontFamily: 'monospace', fontSize: 12 }}>
        <strong>Error de renderizado:</strong><br />
        {this.state.error?.message}<br />
        <pre style={{ fontSize: 10, marginTop: 8, whiteSpace: 'pre-wrap' }}>{this.state.error?.stack?.slice(0, 600)}</pre>
      </div>
    )
    return this.props.children
  }
}

/* ── Bloque contenedor colapsable ────────────────────────────────────────── */
function Bloque({ num, titulo, icon, color, badge, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen)
  const col = color || '#17a2b8'
  return (
    <div className="box mb-3">
      <div
        className="box-header d-flex align-items-center justify-content-between"
        style={{ borderBottom: open ? `3px solid ${col}` : 'none', padding: '12px 16px', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        <div className="d-flex align-items-center">
          <span style={{ width: 24, height: 24, borderRadius: 6, backgroundColor: `${col}18`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginRight: 10, flexShrink: 0 }}>
            <span style={{ fontWeight: 800, fontSize: 11, color: col }}>{num}</span>
          </span>
          {icon && <FontAwesomeIcon icon={icon} style={{ color: col, fontSize: 14, marginRight: 8 }} />}
          <span style={{ fontWeight: 700, fontSize: 14 }}>{titulo}</span>
        </div>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          {badge}
          <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} style={{ color: '#bbb', fontSize: 13 }} />
        </div>
      </div>
      {open && <div className="box-body" style={{ padding: '12px 16px 16px' }}>{children}</div>}
    </div>
  )
}

/* ── Alerta row ──────────────────────────────────────────────────────────── */
const ALERTA_ICONS = {
  baja_respuesta: faPhoneSlash, baja_conversion: faChartLine, asesor_riesgo: faUserTimes,
  avance_semanal: faCalendarWeek, contactabilidad: faClock, cartera_desordenada: faInbox,
  fuente_ineficiente: faFilter, trato_estancado: faPauseCircle, asesor_baja_conv: faHeadset,
}
function AlertaRow({ tipo, mensaje, accion, severity }) {
  const col  = severity === 'green' ? C_V : severity === 'yellow' ? C_A : C_R
  const icon = ALERTA_ICONS[tipo] || faExclamationTriangle
  return (
    <div className="d-flex align-items-start py-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ width: 32, height: 32, borderRadius: 6, backgroundColor: `${col}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 12 }}>
        <FontAwesomeIcon icon={icon} style={{ color: col, fontSize: 13 }} />
      </div>
      <div style={{ flex: 1 }}>
        <p className="mb-0" style={{ fontSize: 13, fontWeight: 600 }}>{mensaje}</p>
        <small className="text-muted">{accion}</small>
      </div>
      <Pill color={col} size={11}>{severity === 'green' ? 'Cumple' : severity === 'yellow' ? 'En riesgo' : 'Urgente'}</Pill>
    </div>
  )
}

/* ── Embudo ejecutivo ────────────────────────────────────────────────────── */
function Embudo({ funnel, metas }) {
  const { tot = 0, resp = 0, trat = 0, reservas = 0, conv = 0 } = funnel
  const steps = [
    { l: 'Leads',     v: tot,      c: '#343a40', note: '100%' },
    { l: 'Respuesta', v: resp,     c: '#007bff', note: `${pct(resp, tot)}% · meta ${metas.pct_respuesta || 35}%`, col: semResp(pct(resp, tot), metas.pct_respuesta || 35) },
    { l: 'Tratos',    v: trat,     c: '#17a2b8', note: `${pct(trat, tot)}% de leads` },
    { l: 'Reservas',  v: reservas, c: '#6f42c1', note: `${pct(reservas, trat)}% de tratos` },
    { l: 'Inscritos', v: conv,     c: '#28a745', note: `${pct(conv, resp)}% · meta ${metas.pct_conversion || 10}%`, col: semConv(pct(conv, resp), metas.pct_conversion || 10) },
  ]
  const max = Math.max(1, tot)
  return (
    <div>
      {steps.map((s, i) => {
        const w    = Math.max(6, (s.v / max) * 100)
        const drop = i > 0 ? steps[i - 1].v - s.v : 0
        return (
          <div key={i} className="d-flex align-items-center mb-2" style={{ gap: 10 }}>
            <span className="text-right" style={{ width: 72, fontSize: 11, fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '.04em', flexShrink: 0 }}>{s.l}</span>
            <div style={{ flex: 1 }}>
              <div style={{ height: 32, background: '#f8f9fa', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${w}%`, height: '100%', backgroundColor: s.c, borderRadius: 4, display: 'flex', alignItems: 'center', paddingLeft: 10, minWidth: 50 }}>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>{fmt(s.v)}</span>
                </div>
              </div>
              {s.note && (
                <div className="d-flex justify-content-between mt-1">
                  <small className="text-muted">{s.note}</small>
                  {drop > 0 && <small className="text-danger">−{fmt(drop)}</small>}
                </div>
              )}
            </div>
            {s.col && <Dot color={s.col} size={10} />}
          </div>
        )
      })}
    </div>
  )
}

/* ── Ranking de asesores ─────────────────────────────────────────────────── */
function Ranking({ asesores, metas }) {
  if (!asesores?.length) return <p className="text-muted mb-0">Sin asesores registrados</p>
  const mM  = metas?.meta_mensual          ?? 25
  const mRS = metas?.meta_reservas_sem     ?? 10
  const mMd = metas?.maduracion_max_dias   ?? 3
  const mCt = metas?.contactabilidad_max   ?? 10
  const mHf = metas?.meta_horas_conv       ?? 4
  const mPr = metas?.procesamiento_max_pct ?? 20
  const mPv = metas?.pct_conversion        ?? 10
  const mRp = metas?.pct_respuesta         ?? 35
  const AVT = ['#007bff', '#fd7e14', '#6f42c1', '#28a745', '#dc3545', '#17a2b8']

  return (
    <div className="table-responsive">
      <table className="table table-bordered table-sm mb-0" style={{ fontSize: 12 }}>
        <thead className="thead-light">
          <tr>
            {['#', 'Asesor', 'Ins./mes', 'Res.sem', 'Conv%', 'Resp%', 'Mad.', 'Cont.', 'Conv.Ef.', 'Sin Clas.', 'Acción'].map(h => (
              <th key={h} className="align-middle text-center" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {asesores.map((a, i) => {
            const respP = pct(+a.resp, +a.leads)
            const convP = pct(+a.conv, +a.resp)
            const cm = +a.conv_mes || 0
            const rs = +a.reservas_semana || 0
            const mad  = a.mad_dias  != null ? +a.mad_dias  : null
            const cont = a.cont_min  != null ? +a.cont_min  : null
            const hef  = +a.conv_ef_hoy  || 0
            const sin  = +a.pct_sin_clas || 0

            const sIns  = semIns(cm, mM)
            const sRes  = rs >= mRS ? C_V : rs >= mRS * .7 ? C_A : C_R
            const sConv = semConv(convP, mPv)
            const sResp = semResp(respP, mRp)
            const sMad  = semMad(mad, mMd)
            const sCont = semCont(cont, mCt)
            const sHef  = semHef(hef, mHf)
            const sSin  = semProc(sin, mPr)

            const accion = cm >= mM    ? 'Mantener ritmo'
              : respP < 25             ? 'Revisar primer contacto'
              : hef < mHf * .5         ? 'Aumentar tiempo en llamadas'
              : convP < 8              ? 'Reforzar cierre'
              :                          `Faltan ${mM - cm} inscritos`

            const c = AVT[i % AVT.length]
            return (
              <tr key={a.id_usuario}>
                <td className="align-middle text-center text-muted" style={{ fontSize: 11, fontWeight: 600 }}>{i + 1}</td>
                <td className="align-middle">
                  <div className="d-flex align-items-center">
                    <div style={{ width: 28, height: 28, borderRadius: 6, backgroundColor: c, color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 8 }}>
                      {ini(a.nombre)}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{a.nombre?.split(' ').filter(Boolean).slice(0, 3).join(' ')}</div>
                      <small className="text-muted">{fmt(a.leads)} leads</small>
                    </div>
                  </div>
                </td>
                <td className="align-middle text-center">
                  <Pill color={sIns}>{cm}/{mM}</Pill>
                  <div style={{ height: 3, backgroundColor: '#eee', borderRadius: 2, marginTop: 3 }}>
                    <div style={{ height: 3, width: `${Math.min(100, (cm / mM) * 100)}%`, backgroundColor: sIns, borderRadius: 2 }} />
                  </div>
                </td>
                <td className="align-middle text-center"><Pill color={sRes}>{rs}/{mRS}</Pill></td>
                <td className="align-middle text-center"><Pill color={sConv}>{convP}%</Pill></td>
                <td className="align-middle text-center"><Pill color={sResp}>{respP}%</Pill></td>
                <td className="align-middle text-center">{mad != null ? <Pill color={sMad}>{mad}d</Pill> : <span className="text-muted">—</span>}</td>
                <td className="align-middle text-center">{cont != null ? <Pill color={sCont}>{cont}min</Pill> : <span className="text-muted">—</span>}</td>
                <td className="align-middle text-center"><Pill color={sHef}>{hef}h</Pill></td>
                <td className="align-middle text-center"><Pill color={sSin}>{sin}%</Pill></td>
                <td className="align-middle" style={{ fontSize: 11, color: '#6c757d', maxWidth: 130, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{accion}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* ── Conv. efectiva ──────────────────────────────────────────────────────── */
function ConvEf({ asesores, meta = 4 }) {
  if (!asesores?.length) return <p className="text-muted mb-0" style={{ fontSize: 12, fontStyle: 'italic' }}>Sin llamadas registradas hoy</p>
  return (
    <div>
      {asesores.map((a, i) => {
        const h   = +a.horas_hoy || 0
        const w   = Math.min(100, (h / meta) * 100)
        const col = semHef(h, meta)
        return (
          <div key={i} className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span style={{ fontSize: 12, fontWeight: 600 }}>{a.nombre?.split(' ').filter(Boolean).slice(0, 3).join(' ')}</span>
              <Pill color={col} size={11}>{h}h / {meta}h</Pill>
            </div>
            <div style={{ height: 6, backgroundColor: '#eee', borderRadius: 3 }}>
              <div style={{ height: 6, width: `${w}%`, backgroundColor: col, borderRadius: 3, transition: 'width .4s' }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ── Highcharts global ───────────────────────────────────────────────────── */
Highcharts.setOptions({
  credits: { enabled: false },
  title:   { text: '' },
  colors:  ['#007bff', '#28a745', '#fd7e14', '#6f42c1', '#dc3545', '#17a2b8', '#ffc107', '#e83e8c'],
  chart:   { style: { fontFamily: 'inherit' }, animation: { duration: 500 } },
  tooltip: { backgroundColor: '#212529', borderRadius: 6, borderWidth: 0, padding: 10, style: { color: '#fff', fontSize: '12px' } },
})

function ChartHistorico({ data, agrupacion }) {
  if (!data?.length) return (
    <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa', borderRadius: 6, border: '1px dashed #dee2e6' }}>
      <div className="text-center px-4">
        <FontAwesomeIcon icon={faChartLine} style={{ fontSize: 32, color: '#ced4da', display: 'block', margin: '0 auto 10px' }} />
        <p className="text-muted mb-1" style={{ fontSize: 13, fontWeight: 600 }}>Sin datos para este filtro</p>
        <p className="text-muted mb-0" style={{ fontSize: 11 }}>No hay leads en el período seleccionado. Prueba cambiando el rango a <strong>Todo</strong>.</p>
      </div>
    </div>
  )
  const MESES = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
  const cats  = data.map(d => {
    const [y, m, dd] = (d.sem_ini || '').split('-')
    return agrupacion === 'mes' ? `${MESES[+m]} ${y?.slice(2)}` : `${dd}/${m}`
  })
  return <HighchartsReact key={`${agrupacion}-${data.length}`} highcharts={Highcharts} options={{
    chart: { type: 'areaspline', backgroundColor: 'transparent', height: 220, margin: [14, 10, 44, 40] },
    xAxis: { categories: cats, labels: { style: { fontSize: '10px' }, step: Math.ceil(data.length / 10) }, lineColor: '#dee2e6', tickColor: 'transparent' },
    yAxis: { gridLineColor: '#f0f0f0', labels: { style: { fontSize: '10px' } }, title: { text: '' } },
    legend: { enabled: true, align: 'left', verticalAlign: 'bottom', itemStyle: { fontSize: '11px', fontWeight: '600' }, symbolRadius: 4 },
    plotOptions: { areaspline: { marker: { enabled: data.length <= 18, radius: 3, lineWidth: 2, lineColor: '#fff' }, lineWidth: 2 } },
    series: [
      { name: 'Leads',     data: data.map(d => +d.leads || 0), color: '#007bff', fillColor: { linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, stops: [[0, '#007bff28'], [1, '#007bff00']] } },
      { name: 'Respuesta', data: data.map(d => +d.resp  || 0), color: '#28a745', fillColor: { linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, stops: [[0, '#28a74528'], [1, '#28a74500']] } },
      { name: 'Inscritos', data: data.map(d => +d.conv  || 0), color: '#fd7e14', fillColor: { linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, stops: [[0, '#fd7e1428'], [1, '#fd7e1400']] } },
    ],
  }} />
}

function ChartFuentes({ fuentes }) {
  if (!fuentes?.length) return <p className="text-muted text-center py-4 mb-0" style={{ fontSize: 13 }}>Sin datos de fuentes</p>
  return <HighchartsReact highcharts={Highcharts} options={{
    chart: { type: 'bar', backgroundColor: 'transparent', height: Math.max(160, fuentes.length * 44 + 60), margin: [10, 14, 30, 12] },
    xAxis: { categories: fuentes.map(f => f.nombre_fuente || 'Sin nombre'), labels: { style: { fontSize: '11px' } }, lineColor: 'transparent', tickColor: 'transparent' },
    yAxis: { gridLineColor: '#f0f0f0', labels: { style: { fontSize: '10px' } }, title: { text: '' } },
    legend: { enabled: true, align: 'right', verticalAlign: 'top', itemStyle: { fontSize: '11px', fontWeight: '600' } },
    plotOptions: { bar: { borderRadius: 3, dataLabels: { enabled: true, style: { fontSize: '10px', fontWeight: '600', textOutline: 'none', color: '#555' } } } },
    series: [
      { name: 'Leads',     data: fuentes.map(f => +f.leads    || 0), color: '#007bff' },
      { name: 'Inscritos', data: fuentes.map(f => +f.inscritos || 0), color: '#28a745' },
    ],
  }} />
}

function ChartProcessing({ estados }) {
  if (!estados?.length) return <p className="text-muted text-center py-4 mb-0" style={{ fontSize: 13 }}>Sin datos de estados</p>
  const COLS = ['#007bff', '#28a745', '#17a2b8', '#6f42c1', '#fd7e14', '#dc3545', '#ffc107', '#6c757d']
  return <HighchartsReact highcharts={Highcharts} options={{
    chart: { type: 'pie', backgroundColor: 'transparent', height: 220, margin: [10, 10, 30, 10] },
    plotOptions: { pie: { innerSize: '55%', borderWidth: 0, dataLabels: { enabled: false }, showInLegend: true } },
    legend: { align: 'right', verticalAlign: 'middle', layout: 'vertical', itemStyle: { fontSize: '10px', fontWeight: '600' } },
    series: [{ name: 'Leads', data: estados.map((e, i) => ({ name: e.nombre_estado || `Estado ${e.id_estado}`, y: +e.cantidad || 0, color: COLS[i % COLS.length] })) }],
  }} />
}

function ChartTratos({ fases }) {
  if (!fases?.length) return <p className="text-muted text-center py-4 mb-0" style={{ fontSize: 13 }}>Sin tratos en pipeline</p>
  const faseColor = n => {
    n = (n || '').toLowerCase()
    if (n.includes('inscrit') || n.includes('cerrad') || n.includes('client')) return '#28a745'
    if (n.includes('reserv'))  return '#6f42c1'
    if (n.includes('perd') || n.includes('cancel')) return '#dc3545'
    if (n.includes('prop'))    return '#fd7e14'
    if (n.includes('contact')) return '#17a2b8'
    return '#007bff'
  }
  return <HighchartsReact highcharts={Highcharts} options={{
    chart: { type: 'bar', backgroundColor: 'transparent', height: Math.max(160, fases.length * 44 + 60), margin: [10, 60, 30, 12] },
    xAxis: { categories: fases.map(f => f.fase), labels: { style: { fontSize: '11px' } }, lineColor: 'transparent', tickColor: 'transparent' },
    yAxis: { gridLineColor: '#f0f0f0', labels: { style: { fontSize: '10px' } }, title: { text: '' } },
    legend: { enabled: false },
    plotOptions: { bar: { borderRadius: 3, colorByPoint: true, colors: fases.map(f => faseColor(f.fase)), dataLabels: { enabled: true, format: '{point.y}', style: { fontSize: '11px', fontWeight: '700', textOutline: 'none', color: '#555' } } } },
    series: [{ name: 'Tratos', data: fases.map(f => +f.cantidad || 0) }],
  }} />
}

function ChartCierre({ cierres, meses }) {
  if (!cierres?.length || !meses?.length) return <p className="text-muted text-center py-4 mb-0" style={{ fontSize: 13 }}>Sin datos de cierre mensual</p>
  const cols = meses.length === 3 ? [
    { key: 'conv_ant2', label: mesLbl(meses[0]), color: '#dee2e6' },
    { key: 'conv_ant',  label: mesLbl(meses[1]), color: '#90c0f8' },
    { key: 'conv_mes',  label: mesLbl(meses[2]), color: '#007bff' },
  ] : []
  return <HighchartsReact highcharts={Highcharts} options={{
    chart: { type: 'column', backgroundColor: 'transparent', height: 240, margin: [14, 10, 58, 36] },
    xAxis: { categories: cierres.map(c => c.nombre?.split(' ')[0] || c.nombre || '?'), labels: { style: { fontSize: '10px', fontWeight: '600' }, rotation: -30 }, lineColor: '#dee2e6', tickColor: 'transparent' },
    yAxis: { gridLineColor: '#f0f0f0', labels: { style: { fontSize: '10px' } }, title: { text: '' } },
    legend: { enabled: true, align: 'center', verticalAlign: 'bottom', itemStyle: { fontSize: '11px', fontWeight: '600' } },
    plotOptions: { column: { borderRadius: 3, groupPadding: .1, borderWidth: 0 } },
    series: cols.map(c => ({ name: c.label, data: cierres.map(r => +r[c.key] || 0), color: c.color })),
  }} />
}

function ChartLeadTipos({ tipos }) {
  if (!tipos?.length) return <p className="text-muted text-center py-4 mb-0" style={{ fontSize: 13 }}>Sin clasificación disponible</p>
  if (tipos.length === 1) {
    const t   = tipos[0]
    const hot = t.tipo === 'Caliente'
    return (
      <div className="text-center py-3">
        <FontAwesomeIcon icon={hot ? faFire : faSnowflake}
          style={{ color: hot ? '#fd7e14' : '#6c757d', fontSize: 30, display: 'block', margin: '0 auto 8px' }} />
        <p className="mb-1" style={{ fontWeight: 700, fontSize: 14 }}>Todos los leads son <span style={{ color: hot ? '#fd7e14' : '#6c757d' }}>{t.tipo}</span></p>
        <small className="text-muted" style={{ fontSize: 12 }}>
          {hot ? 'Todos captados por ManyChat' : 'Sin leads captados por ManyChat en esta campaña'}
        </small>
      </div>
    )
  }
  return <HighchartsReact highcharts={Highcharts} options={{
    chart: { type: 'pie', backgroundColor: 'transparent', height: 200, margin: [10, 10, 30, 10] },
    plotOptions: { pie: { innerSize: '55%', borderWidth: 0, dataLabels: { enabled: true, format: '<b>{point.name}</b><br/>{point.percentage:.0f}%', style: { fontSize: '11px', textOutline: 'none' } }, showInLegend: false } },
    series: [{ name: 'Leads', data: tipos.map(t => ({ name: `Lead ${t.tipo}`, y: +t.leads || 0, color: t.tipo === 'Caliente' ? '#fd7e14' : '#6c757d' })) }],
  }} />
}

/* ── Modal metas ─────────────────────────────────────────────────────────── */
function ModalMetas({ idCamp, metasActuales, asesores, fuentes, onClose, onSaved }) {
  const [tabModal, setTabModal] = useState('metas')
  const [form, setForm] = useState({
    pct_respuesta_objetivo:             metasActuales?.pct_respuesta         ?? 35,
    pct_conversion_objetivo:            metasActuales?.pct_conversion        ?? 10,
    meta_llamadas_diarias:              metasActuales?.meta_marcaciones_dia  ?? 90,
    meta_tratos_diarios:                metasActuales?.meta_tratos_dia       ?? 3,
    meta_cierres_diarios:               metasActuales?.meta_cierres_dia      ?? 2,
    meta_conversiones_mensuales_asesor: metasActuales?.meta_mensual          ?? 25,
    total_leads:                        metasActuales?.total_leads           ?? 0,
    meta_horas_conv:                    metasActuales?.meta_horas_conv       ?? 4,
    meta_inscritos_sem:                 metasActuales?.meta_inscritos_sem    ?? 6,
    meta_reservas_sem:                  metasActuales?.meta_reservas_sem     ?? 10,
    maduracion_max_dias:                metasActuales?.maduracion_max_dias   ?? 3,
    contactabilidad_max:                metasActuales?.contactabilidad_max   ?? 10,
    procesamiento_max_pct:              metasActuales?.procesamiento_max_pct ?? 20,
  })
  const [saving,     setSaving]     = useState(false)
  const [msg,        setMsg]        = useState(null)
  const [configData, setConfigData] = useState(null)
  const [cfgSaving,  setCfgSaving]  = useState(false)
  const [cfgMsg,     setCfgMsg]     = useState(null)

  useEffect(() => {
    if (!idCamp) {
      setConfigData({ asesores: [], fuentes: [] })
      return
    }
    authAxios.get(`/admin/kpis-ventas/config?id_camp=${idCamp}`)
      .then(r => {
        const data = r.data?.data
        setConfigData({
          asesores: (data?.asesores || []).map(a => ({ ...a, activo: +a.activo })),
          fuentes:  (data?.fuentes  || []).map(f => ({ ...f, activo: +f.activo })),
        })
      })
      .catch(() => setConfigData({ asesores: [], fuentes: [] }))
  }, [idCamp])

  const toggleAsesor = id => setConfigData(p => ({
    ...p,
    asesores: p.asesores.map(a => a.id_usuario == id ? { ...a, activo: +a.activo ? 0 : 1 } : a)
  }))

  const toggleFuente = id => setConfigData(p => ({
    ...p,
    fuentes: p.fuentes.map(f => f.id_fuente == id ? { ...f, activo: +f.activo ? 0 : 1 } : f)
  }))

  const saveConfig = async () => {
    if (!idCamp || !configData) return
    setCfgSaving(true); setCfgMsg(null)
    try {
      const r = await authAxios.post('/admin/kpis-ventas/config', {
        id_camp:  idCamp,
        asesores: configData.asesores.map(a => ({ id_usuario: a.id_usuario, activo: a.activo })),
        fuentes:  configData.fuentes.map(f => ({ id_fuente: f.id_fuente, activo: f.activo })),
      })
      if (r.data?.success) {
        setCfgMsg({ ok: true, text: 'Configuración guardada' })
        setTimeout(() => { onSaved(); onClose() }, 900)
      } else setCfgMsg({ ok: false, text: r.data?.error || 'Error al guardar' })
    } catch { setCfgMsg({ ok: false, text: 'Error de conexión' }) }
    finally { setCfgSaving(false) }
  }

  const save = async () => {
    if (!idCamp) { setMsg({ ok: false, text: 'Sin campaña activa' }); return }
    setSaving(true); setMsg(null)
    try {
      const r = await authAxios.post('/admin/kpis-ventas/metas', { id_camp: idCamp, ...form })
      if (r.data?.success) {
        setMsg({ ok: true, text: 'Metas guardadas correctamente' })
        setTimeout(() => { onSaved(); onClose() }, 900)
      } else setMsg({ ok: false, text: r.data?.error || 'Error al guardar' })
    } catch { setMsg({ ok: false, text: 'Error de conexión' }) }
    finally { setSaving(false) }
  }

  const groups = [
    { title: 'Campaña', fields: [
      { k: 'pct_respuesta_objetivo',             l: 'Respuesta objetivo (%)',      min: 1,  max: 100   },
      { k: 'pct_conversion_objetivo',            l: 'Conversión objetivo (%)',     min: 1,  max: 100   },
      { k: 'meta_conversiones_mensuales_asesor', l: 'Inscritos/mes por asesor',   min: 1,  max: 200   },
      { k: 'total_leads',                        l: 'Total leads proyectados',    min: 0,  max: 99999 },
    ]},
    { title: 'Semanal', fields: [
      { k: 'meta_inscritos_sem', l: 'Inscritos por semana', min: 1, max: 50  },
      { k: 'meta_reservas_sem',  l: 'Reservas por semana',  min: 1, max: 100 },
    ]},
    { title: 'Diaria', fields: [
      { k: 'meta_llamadas_diarias', l: 'Llamadas/día',              min: 1, max: 500 },
      { k: 'meta_tratos_diarios',   l: 'Tratos/día',                min: 1, max: 50  },
      { k: 'meta_cierres_diarios',  l: 'Cierres/día',               min: 1, max: 50  },
      { k: 'meta_horas_conv',       l: 'Conv. efectiva (h/asesor)', min: 1, max: 12  },
    ]},
    { title: 'Umbrales de calidad', fields: [
      { k: 'maduracion_max_dias',   l: 'Maduración máx (días)',      min: 1, max: 30  },
      { k: 'contactabilidad_max',   l: 'Contactabilidad máx (min)',  min: 1, max: 120 },
      { k: 'procesamiento_max_pct', l: 'Sin clasificar máx (%)',     min: 1, max: 100 },
    ]},
  ]

  return (
    <Modal show onHide={onClose} size="md" centered>
      <Modal.Header closeButton style={{ padding: '12px 16px' }}>
        <Modal.Title style={{ fontSize: 15, fontWeight: 700 }}>
          <FontAwesomeIcon icon={faSlidersH} className="mr-2 text-primary" />
          Configuración de campaña
        </Modal.Title>
      </Modal.Header>
      {!idCamp && (
        <div className="alert alert-warning mx-3 mt-3 mb-0 py-2" style={{ fontSize: 12 }}>
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
          Sin campaña activa — los cambios no se guardarán
        </div>
      )}
      {/* Tabs del modal */}
      <div className="px-3 pt-3 pb-0" style={{ borderBottom: '1px solid #dee2e6' }}>
        <div className="btn-group btn-group-sm">
          {[
            { k: 'metas',   l: 'Metas',   icon: faSlidersH },
            { k: 'equipo',  l: 'Equipo',  icon: faIdCard   },
            { k: 'fuentes', l: 'Fuentes', icon: faFilter   },
          ].map(t => (
            <button key={t.k} onClick={() => setTabModal(t.k)}
              className={`btn btn-${tabModal === t.k ? 'primary' : 'outline-secondary'}`}
              style={{ fontSize: 12 }}>
              <FontAwesomeIcon icon={t.icon} className="mr-1" style={{ fontSize: 11 }} />{t.l}
            </button>
          ))}
        </div>
      </div>
      <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {/* Tab Metas */}
        {tabModal === 'metas' && (<>
          {groups.map(g => (
            <div key={g.title} className="mb-3">
              <p className="text-muted mb-2" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>{g.title}</p>
              {g.fields.map(f => (
                <div key={f.k} className="d-flex align-items-center mb-2 px-2 py-2" style={{ background: '#f8f9fa', borderRadius: 4 }}>
                  <label className="mb-0 flex-grow-1" style={{ fontSize: 13, fontWeight: 500, color: '#495057' }}>{f.l}</label>
                  <input
                    type="number" value={form[f.k]} min={f.min} max={f.max}
                    onChange={e => setForm(p => ({ ...p, [f.k]: +e.target.value }))}
                    className="form-control text-right"
                    style={{ width: 80, fontSize: 14, fontWeight: 700, color: '#007bff' }}
                  />
                </div>
              ))}
            </div>
          ))}
          {msg && (
            <div className={`alert ${msg.ok ? 'alert-success' : 'alert-danger'} py-2`} style={{ fontSize: 13 }}>
              <FontAwesomeIcon icon={msg.ok ? faCheckCircle : faExclamationTriangle} className="mr-2" />
              {msg.text}
            </div>
          )}
        </>)}
        {/* Tab Equipo */}
        {tabModal === 'equipo' && (
          <div>
            <p className="text-muted mb-2" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
              Asesores del programa — activa/desactiva para incluir en el reporte
            </p>
            {!configData
              ? <p className="text-muted text-center py-3" style={{ fontSize: 13 }}>Cargando...</p>
              : !configData.asesores?.length
              ? <p className="text-muted text-center py-3" style={{ fontSize: 13 }}>Sin asesores registrados</p>
              : configData.asesores.map((a, i) => {
                  const stats = asesores?.find(x => +x.id_usuario === +a.id_usuario)
                  const conv  = +(stats?.conv_mes || 0)
                  const col   = +a.activo ? '#212529' : '#adb5bd'
                  return (
                    <div key={i} className="d-flex align-items-center mb-2 px-2 py-2"
                      style={{ background: +a.activo ? '#f8f9fa' : '#f0f0f0', borderRadius: 4, opacity: +a.activo ? 1 : 0.6 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: `${+a.activo ? '#007bff' : '#adb5bd'}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10, flexShrink: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: +a.activo ? '#007bff' : '#adb5bd' }}>
                          {(a.nombre || '?').split(' ').filter(Boolean).slice(0, 2).map(x => x[0]?.toUpperCase() || '').join('')}
                        </span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: col, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.nombre}</div>
                        {stats && <small className="text-muted" style={{ fontSize: 11 }}>{stats.leads || 0} leads · {stats.resp || 0} resp · {conv} conv</small>}
                      </div>
                      <div className="custom-control custom-switch ml-2" style={{ flexShrink: 0 }}>
                        <input type="checkbox" className="custom-control-input" id={`as-${a.id_usuario}`}
                          checked={!!+a.activo} onChange={() => toggleAsesor(a.id_usuario)} />
                        <label className="custom-control-label" htmlFor={`as-${a.id_usuario}`}></label>
                      </div>
                    </div>
                  )
                })
            }
            {cfgMsg && tabModal === 'equipo' && (
              <div className={`alert ${cfgMsg.ok ? 'alert-success' : 'alert-danger'} py-2 mt-2`} style={{ fontSize: 13 }}>
                <FontAwesomeIcon icon={cfgMsg.ok ? faCheckCircle : faExclamationTriangle} className="mr-2" />{cfgMsg.text}
              </div>
            )}
          </div>
        )}
        {/* Tab Fuentes */}
        {tabModal === 'fuentes' && (
          <div>
            <p className="text-muted mb-2" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
              Fuentes del programa — activa/desactiva para incluir en el reporte
            </p>
            {!configData
              ? <p className="text-muted text-center py-3" style={{ fontSize: 13 }}>Cargando...</p>
              : !configData.fuentes?.length
              ? <p className="text-muted text-center py-3" style={{ fontSize: 13 }}>Sin fuentes registradas</p>
              : configData.fuentes.map((f, i) => {
                  const stats    = fuentes?.find(x => +x.id_fuente === +f.id_fuente)
                  const leads    = +(stats?.leads     || 0)
                  const inscritos= +(stats?.inscritos || 0)
                  const pctF     = +(stats?.pct_conv  || 0)
                  const col      = pctF >= 10 ? '#28a745' : pctF >= 5 ? '#fd7e14' : '#dc3545'
                  return (
                    <div key={i} className="d-flex align-items-center mb-2 px-2 py-2"
                      style={{ background: +f.activo ? '#f8f9fa' : '#f0f0f0', borderRadius: 4, opacity: +f.activo ? 1 : 0.6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: +f.activo ? '#212529' : '#adb5bd' }}>{f.nombre_fuente}</div>
                        {stats && <small className="text-muted" style={{ fontSize: 11 }}>{leads} leads · {inscritos} ins · {pctF}% conv</small>}
                      </div>
                      <span className="badge mr-3" style={{ backgroundColor: `${col}20`, color: col, fontSize: 12, fontWeight: 700 }}>{pctF}%</span>
                      <div className="custom-control custom-switch" style={{ flexShrink: 0 }}>
                        <input type="checkbox" className="custom-control-input" id={`fu-${f.id_fuente}`}
                          checked={!!+f.activo} onChange={() => toggleFuente(f.id_fuente)} />
                        <label className="custom-control-label" htmlFor={`fu-${f.id_fuente}`}></label>
                      </div>
                    </div>
                  )
                })
            }
            {cfgMsg && tabModal === 'fuentes' && (
              <div className={`alert ${cfgMsg.ok ? 'alert-success' : 'alert-danger'} py-2 mt-2`} style={{ fontSize: 13 }}>
                <FontAwesomeIcon icon={cfgMsg.ok ? faCheckCircle : faExclamationTriangle} className="mr-2" />{cfgMsg.text}
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ padding: '10px 16px' }}>
        <Button variant="outline-secondary" size="sm" onClick={onClose}>Cerrar</Button>
        {tabModal === 'metas' && (
          <Button variant="primary" size="sm" onClick={save} disabled={saving || !idCamp}>
            {saving
              ? <><FontAwesomeIcon icon={faSpinner} spin className="mr-1" />Guardando...</>
              : <><FontAwesomeIcon icon={faSave} className="mr-1" />Guardar metas</>
            }
          </Button>
        )}
        {(tabModal === 'equipo' || tabModal === 'fuentes') && (
          <Button variant="primary" size="sm" onClick={saveConfig} disabled={cfgSaving || !idCamp || !configData}>
            {cfgSaving
              ? <><FontAwesomeIcon icon={faSpinner} spin className="mr-1" />Guardando...</>
              : <><FontAwesomeIcon icon={faSave} className="mr-1" />Guardar configuración</>
            }
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

/* ══════════════════════════════════════════════════════════════════════════
   PÁGINA PRINCIPAL
══════════════════════════════════════════════════════════════════════════ */
const ReporteKpisVentas = () => {
  const [proyectos,   setProyectos]   = useState([])
  const [globalKpis,  setGlobalKpis]  = useState(null)
  const [pidSel,      setPidSel]      = useState(null)
  const [prodData,    setProdData]    = useState(null)
  const [loading,     setLoading]     = useState(true)
  const [loadingProd, setLoadingProd] = useState(false)
  const [error,       setError]       = useState(null)
  const [modalMetas,  setModalMetas]  = useState(false)
  const [histFiltros, setHistFiltros] = useState({ agrupacion: 'semana', asesor: 0, fuente: 0, preset: '90d', tipo_lead: '', estado_lead: '' })
  const [histData,    setHistData]    = useState([])
  const [histLoading, setHistLoading] = useState(false)
  const [histError,   setHistError]   = useState(null)

  useEffect(() => {
    Promise.all([authAxios.get('/admin/kpis-ventas/proyectos'), authAxios.get('/admin/kpis-ventas/global')])
      .then(([rP, rG]) => {
        const proy = rP.data?.data ?? []
        setProyectos(proy); setGlobalKpis(rG.data?.data ?? null)
        if (proy.length > 0) setPidSel(proy[0].id_proyecto)
      })
      .catch(() => setError('No se pudo conectar con la API'))
      .finally(() => setLoading(false))
  }, [])

  const cargarProducto = useCallback(pid => {
    if (!pid) return
    setLoadingProd(true); setProdData(null)
    authAxios.get(`/admin/kpis-ventas/producto?pid=${pid}`)
      .then(r => setProdData(r.data?.data ?? null))
      .catch(() => setProdData(null))
      .finally(() => setLoadingProd(false))
  }, [])

  useEffect(() => {
    if (pidSel) {
      cargarProducto(pidSel)
      setHistFiltros(f => ({ ...f, asesor: 0, fuente: 0, tipo_lead: '', estado_lead: '' }))
    }
  }, [pidSel, cargarProducto])

  const cargarHistorico = useCallback((pid, f, campInicio) => {
    if (!pid) return
    setHistLoading(true)
    setHistError(null)
    const hoy   = new Date()
    const hasta = hoy.toISOString().slice(0, 10)
    let desde
    if (f.preset === 'todo') {
      desde = campInicio ? campInicio.slice(0, 10) : new Date(hoy - 730 * 864e5).toISOString().slice(0, 10)
    } else {
      const dias = ({ '7d': 7, '30d': 30, '60d': 60, '90d': 90 })[f.preset] ?? 90
      desde = new Date(hoy - dias * 864e5).toISOString().slice(0, 10)
    }
    const q = new URLSearchParams({ pid, agrupacion: f.agrupacion, desde, hasta, ...(f.asesor > 0 ? { asesor: f.asesor } : {}), ...(f.fuente > 0 ? { fuente: f.fuente } : {}), ...(f.tipo_lead ? { tipo_lead: f.tipo_lead } : {}), ...(f.estado_lead ? { estado_lead: f.estado_lead } : {}) })
    authAxios.get(`/admin/kpis-ventas/historico?${q}`)
      .then(r => {
        if (r.data?.error) {
          setHistError(`Error del servidor: ${r.data.error}`)
          setHistData([])
        } else {
          setHistData(r.data?.data ?? [])
        }
      })
      .catch(err => {
        const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Error de conexión'
        setHistError(`${err.response?.status || ''} ${msg}`.trim())
        setHistData([])
      })
      .finally(() => setHistLoading(false))
  }, [])

  useEffect(() => {
    if (pidSel) {
      const prog = proyectos.find(p => p.id_proyecto === pidSel)
      cargarHistorico(pidSel, histFiltros, prog?.camp_inicio)
    }
  }, [pidSel, histFiltros, cargarHistorico, proyectos])

  /* extraer datos */
  const g          = globalKpis || {}
  const d          = prodData   || {}
  const funnel     = d.funnel            || {}
  const metas      = d.metas             || {}
  const asesores   = d.asesores          || []
  const tratosFase = d.tratos_fase       || []
  const cierres    = d.cierres           || []
  const meses      = d.meses             || []
  const avanceSem  = d.avance_semanal    || {}
  const maduracion = d.maduracion        || {}
  const contactab  = d.contactabilidad   || {}
  const convEf     = d.conv_efectiva     || {}
  const prodHoy    = d.productividad_hoy || {}
  const procesam   = d.procesamiento     || []
  const fuentes    = d.fuentes           || []
  const tiposLead  = d.tipos_lead        || []
  const alertas    = d.alertas           || []

  const totLeads   = +funnel.tot  || 0
  const totResp    = +funnel.resp || 0
  const totConv    = +funnel.conv || 0
  const pctResp    = pct(totResp, totLeads)
  const pctConv    = pct(totConv, totResp)
  const metaMes    = metas.meta_mensual       ?? 25
  const metaInsSem = metas.meta_inscritos_sem ?? 6
  const metaResSem = metas.meta_reservas_sem  ?? 10
  const totalMeta  = metaMes * (asesores.length || 1)
  const totalConvM = asesores.reduce((s, a) => s + (+a.conv_mes || 0), 0)
  const madDias    = maduracion.promedio_dias    != null ? +maduracion.promedio_dias    : null
  const contMin    = contactab.min_promedio      != null ? +contactab.min_promedio      : null
  const contPct    = contactab.pct_a_tiempo     != null ? +contactab.pct_a_tiempo     : null
  const horasConv  = convEf.horas_hoy            != null ? +convEf.horas_hoy            : null
  const hXAs       = convEf.horas_x_asesor       != null ? +convEf.horas_x_asesor       : null
  const pctSinClas = (() => {
    const tot    = procesam.reduce((s, e) => s + (+e.cantidad || 0), 0)
    const sinSum = procesam
      .filter(e => +e.id_estado === 0 || +e.id_estado === 1
        || (e.nombre_estado || '').toLowerCase().includes('sin')
        || (e.nombre_estado || '').toLowerCase().includes('nuevo')
        || (e.nombre_estado || '').toLowerCase().includes('pendiente'))
      .reduce((s, e) => s + (+e.cantidad || 0), 0)
    return tot > 0 ? pct(sinSum, tot) : 0
  })()

  const prodActual = proyectos.find(p => p.id_proyecto === pidSel)
  const sIns  = semIns(totalConvM, totalMeta)
  const sResp = semResp(pctResp, metas.pct_respuesta || 35)
  const sConv = semConv(pctConv, metas.pct_conversion || 10)

  return (
    <Layout>
      <div className="ControlReportes">
        <div className="ControlReportes__header">
          <HeaderContent title="Reporte Comercial - KPIs" />
          <Breadcrumb link={[{ url: '', nombre: 'Comercial' }, { url: '', nombre: 'Reportes' }, { url: '', nombre: 'KPIs' }]} />
        </div>

        <div className="ControlReportes__body">
          <SpinnerLoading loading={loading}>

            {error && <div className="alert alert-danger">{error}</div>}

            {!error && (<>

              {/* ── Barra superior ── */}
              <div className="box p-3 mb-3">
                <div className="d-flex flex-wrap align-items-center" style={{ gap: 16 }}>
                  {/* Stats globales */}
                  <div className="d-flex flex-wrap" style={{ gap: 16, flex: '1 1 auto' }}>
                    {[
                      { icon: faUsers,      label: 'Total leads',   value: fmt(g.total_leads),   color: '#343a40' },
                      { icon: faPhone,      label: 'Con respuesta', value: fmt(g.con_respuesta),  color: '#007bff' },
                      { icon: faUserCheck,  label: 'Convertidos',   value: fmt(g.convertidos),    color: '#28a745' },
                      { icon: faLayerGroup, label: 'Programas',     value: proyectos.length,       color: '#6f42c1' },
                    ].map((x, i) => (
                      <div key={x.label} className="d-flex align-items-center" style={{ gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: `${x.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <FontAwesomeIcon icon={x.icon} style={{ color: x.color, fontSize: 15 }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 20, color: x.color, lineHeight: 1 }}>{x.value}</div>
                          <small className="text-muted" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em' }}>{x.label}</small>
                        </div>
                        {i < 3 && <div style={{ width: 1, height: 30, backgroundColor: '#e9ecef', marginLeft: 4 }} />}
                      </div>
                    ))}
                  </div>

                  {/* Combobox + acciones */}
                  <div className="d-flex align-items-center" style={{ gap: 8, flexShrink: 0 }}>
                    <div>
                      <small className="text-muted d-block mb-1" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em' }}>Programa</small>
                      <ProgramCombobox proyectos={proyectos} pidSel={pidSel} onChange={setPidSel} />
                    </div>
                    <div className="d-flex align-items-end" style={{ gap: 4, paddingBottom: 2 }}>
                      {prodData?.id_camp && (
                        <Button variant="outline-secondary" size="sm" onClick={() => setModalMetas(true)} title="Configurar metas y equipo">
                          <FontAwesomeIcon icon={faSlidersH} />
                        </Button>
                      )}
                      <Button variant="outline-secondary" size="sm" onClick={() => pidSel && cargarProducto(pidSel)} title="Actualizar">
                        <FontAwesomeIcon icon={faSyncAlt} />
                      </Button>
                      <Button variant="outline-secondary" size="sm" title="Exportar CSV" onClick={() => {
                        if (!prodData) return
                        const fecha    = new Date().toISOString().slice(0, 10)
                        const q = s => `"${String(s ?? '').replace(/"/g, '""')}"`
                        // ── Sección 1: KPIs del programa ──────────────────────
                        const kpiRows = [
                          ['REPORTE KPI\'S DE VENTAS'],
                          ['Programa', q(prodActual?.nombre_proyecto || '')],
                          ['Fecha de exportación', fecha],
                          [],
                          ['RESUMEN EJECUTIVO'],
                          ['Métrica', 'Valor', 'Meta', 'Estado'],
                          ['Total leads campaña',   totLeads,   '',                         ''],
                          ['% Respuesta',           `${pctResp}%`, `${metas.pct_respuesta || 35}%`, pctResp >= (metas.pct_respuesta||35) ? 'OK' : 'Revisar'],
                          ['% Conversión',          `${pctConv}%`, `${metas.pct_conversion || 10}%`, pctConv >= (metas.pct_conversion||10) ? 'OK' : 'Revisar'],
                          ['Inscritos mes actual',  totalConvM, totalMeta,                  totalConvM >= totalMeta ? 'OK' : `Faltan ${totalMeta - totalConvM}`],
                          ['Contactabilidad prom.', contMin != null ? `${contMin} min` : 'S/D', `≤${metas.contactabilidad_max || 10} min`, contMin != null ? (contMin <= (metas.contactabilidad_max||10) ? 'OK' : 'Alto') : ''],
                          ['% Contactados a tiempo', contPct != null ? `${contPct}%` : 'S/D', `≥50%`, contPct != null ? (contPct >= 50 ? 'OK' : 'Bajo') : ''],
                          ['Maduración prom.',      madDias != null ? `${madDias} d` : 'S/D', `≤${metas.maduracion_max_dias || 3} d`, madDias != null ? (madDias <= (metas.maduracion_max_dias||3) ? 'OK' : 'Alto') : ''],
                          ['Conv. efectiva hoy',    horasConv != null ? `${horasConv} h` : '0 h', `≥${metas.meta_horas_conv || 4} h/asesor`, ''],
                          ['Tratos hoy',            prodHoy.tratos_hoy || 0, `≥${metas.meta_tratos_dia || 3}`, ''],
                          ['Marcaciones hoy',       prodHoy.marcaciones_hoy || 0, `≥${metas.meta_marcaciones_dia || 90}`, ''],
                          ['Sin clasificar',        `${pctSinClas}%`, `≤${metas.procesamiento_max_pct || 20}%`, pctSinClas <= (metas.procesamiento_max_pct||20) ? 'OK' : 'Alto'],
                          [],
                          // ── Sección 2: Ranking de asesores ────────────────
                          ['RANKING DE ASESORES'],
                          ['Asesor','Leads','Respondió','Inscritos mes','Ins. histórico','Tratos hoy','Marcaciones hoy','Conv. efectiva hoy (h)','Maduración (d)','Contactabilidad (min)','% Sin clasificar'],
                          ...asesores.map(a => [
                            q(a.nombre), a.leads||0, a.resp||0, a.conv_mes||0, a.conv||0,
                            a.tratos_hoy||0, a.marcaciones_hoy||0, a.conv_ef_hoy||0,
                            a.mad_dias ?? '', a.cont_min ?? '', a.pct_sin_clas ?? 0,
                          ]),
                        ]
                        const csv  = kpiRows.map(r => r.join(',')).join('\n')
                        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
                        const url  = URL.createObjectURL(blob)
                        const el   = document.createElement('a')
                        el.href = url; el.download = `kpis-${(prodActual?.nombre_proyecto||'ventas').slice(0,40)}-${fecha}.csv`
                        el.click(); URL.revokeObjectURL(url)
                      }}>
                        <FontAwesomeIcon icon={faFileDownload} />
                      </Button>
                      <Button variant="outline-secondary" size="sm" title="Exportar PDF" onClick={() => {
                        if (!prodData) return
                        const fecha  = new Date().toLocaleDateString('es-PE')
                        const sem    = c => c === '#28a745' ? '#28a745' : c === '#fd7e14' ? '#fd7e14' : c === '#dc3545' ? '#dc3545' : '#6c757d'
                        const kpiRows = [
                          ['Inscritos mes', `${totalConvM}/${totalMeta}`, `${totalMeta}`, sIns],
                          ['Conversión',    `${pctConv}%`,   `${metas.pct_conversion||10}%`,  sConv],
                          ['Respuesta',     `${pctResp}%`,   `${metas.pct_respuesta||35}%`,   sResp],
                          ['Maduración',    madDias!=null?`${madDias}d`:'S/D',  `≤${metas.maduracion_max_dias||3}d`,  semMad(madDias, metas.maduracion_max_dias||3)],
                          ['Contactabilidad',contMin!=null?`${contMin}min`:'S/D',`≤${metas.contactabilidad_max||10}min`, semCont(contMin, metas.contactabilidad_max||10)],
                          ['% a tiempo',   contPct!=null?`${contPct}%`:'S/D',  '≥50%',   contPct!=null?(contPct>=50?C_V:C_R):C_G],
                          ['Sin clasificar',`${pctSinClas}%`,`≤${metas.procesamiento_max_pct||20}%`, semProc(pctSinClas, metas.procesamiento_max_pct||20)],
                        ].map(([k,v,m,c]) => `<tr><td>${k}</td><td style="color:${sem(c)};font-weight:700">${v}</td><td style="color:#888">${m}</td></tr>`).join('')
                        const rankRows = asesores.map(a => {
                          const ins = +a.conv_mes||0; const meta = metas.meta_mensual||25
                          const col = ins>=meta?'#28a745':ins>=meta*.8?'#fd7e14':'#dc3545'
                          return `<tr><td>${a.nombre?.split(' ').slice(0,3).join(' ')}</td><td style="color:${col};font-weight:700">${ins}/${meta}</td><td>${+a.reservas_semana||0}/${metas.meta_reservas_sem||10}</td><td>${+a.pct_conv||0}%</td><td>${+a.pct_resp||0}%</td></tr>`
                        }).join('')
                        const fuenteRows = fuentes.map(f => `<tr><td>${f.nombre_fuente}</td><td>${f.leads}</td><td>${f.respondidos}</td><td>${f.pct_resp}%</td><td>${f.inscritos}</td><td>${f.pct_conv}%</td></tr>`).join('')
                        const alertaRows = alertas.map(a => `<tr><td>${a.mensaje}</td><td>${a.accion}</td></tr>`).join('')
                        const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>KPIs Ventas — ${prodActual?.nombre_proyecto||''}</title>
                        <style>body{font-family:Arial,sans-serif;font-size:12px;color:#212529;margin:24px}h1{font-size:16px;margin-bottom:4px}h2{font-size:13px;margin:18px 0 6px;border-bottom:2px solid #007bff;padding-bottom:4px;color:#007bff}table{width:100%;border-collapse:collapse;margin-bottom:8px}th{background:#f0f4ff;text-align:left;padding:5px 8px;font-size:11px}td{padding:4px 8px;border-bottom:1px solid #eee;font-size:11px}.footer{color:#888;font-size:10px;margin-top:24px}@media print{body{margin:0}}</style>
                        </head><body>
                        <h1>KPIs de Ventas — ${prodActual?.nombre_proyecto||''}</h1><p style="color:#888;font-size:11px;margin-top:0">Exportado el ${fecha}</p>
                        <h2>Resumen Ejecutivo</h2><table><tr><th>KPI</th><th>Resultado</th><th>Meta</th></tr>${kpiRows}</table>
                        <h2>Ranking de Asesores</h2><table><tr><th>Asesor</th><th>Inscritos</th><th>Reservas sem.</th><th>Conversión</th><th>Respuesta</th></tr>${rankRows}</table>
                        <h2>Rendimiento por Fuente</h2><table><tr><th>Fuente</th><th>Leads</th><th>Respondidos</th><th>% Resp.</th><th>Inscritos</th><th>% Conv.</th></tr>${fuenteRows}</table>
                        ${alertas.length?`<h2>Alertas Activas</h2><table><tr><th>Alerta</th><th>Acción sugerida</th></tr>${alertaRows}</table>`:''}
                        <p class="footer">Generado por Sistema INEDI • ${fecha}</p>
                        </body></html>`
                        const w = window.open('', '_blank', 'width=900,height=700')
                        w.document.write(html); w.document.close(); w.focus()
                        setTimeout(() => { w.print() }, 400)
                      }}>
                        <FontAwesomeIcon icon={faPrint} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <SpinnerLoading loading={loadingProd}>
                <ErrorBoundary key={pidSel}>
                {prodData && (<>

                  {/* Header programa seleccionado */}
                  <div className="box p-3 mb-3" style={{ borderLeft: '4px solid #17a2b8' }}>
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: 20 }}>
                      <div style={{ flex: '1 1 200px' }}>
                        <small className="text-muted d-block" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em' }}>Programa activo</small>
                        <h5 className="mb-0" style={{ fontWeight: 700 }}>{prodActual?.nombre_proyecto}</h5>
                        {prodActual?.camp_inicio && (
                          <small className="text-muted">Desde {prodActual.camp_inicio.slice(0, 10).split('-').reverse().join('/')}</small>
                        )}
                      </div>
                      <div className="d-flex flex-wrap" style={{ gap: 20 }}>
                        {[
                          { l: 'Leads',      v: fmt(totLeads),    sem: null,  sub: 'total campaña' },
                          { l: 'Respuesta',  v: `${pctResp}%`,   sem: sResp, sub: `meta ${metas.pct_respuesta || 35}%` },
                          { l: 'Conversión', v: `${pctConv}%`,   sem: sConv, sub: `meta ${metas.pct_conversion || 10}%` },
                          { l: 'Inscritos',  v: fmt(totalConvM), sem: sIns,  sub: `de ${fmt(totalMeta)} meta` },
                          { l: 'Tratos',     v: fmt(funnel.trat || 0), sem: null, sub: 'en pipeline' },
                        ].map(x => (
                          <div key={x.l} className="text-center">
                            <div className="d-flex align-items-center justify-content-center" style={{ gap: 4 }}>
                              {x.sem && <Dot color={x.sem} size={8} />}
                              <span style={{ fontWeight: 700, fontSize: 20, color: x.sem || '#212529' }}>{x.v}</span>
                            </div>
                            <small className="text-muted d-block" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em' }}>{x.l}</small>
                            <small className="text-muted" style={{ fontSize: 10 }}>{x.sub}</small>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ── 1: Alertas (primero — visibilidad inmediata) ── */}
                  {alertas.length > 0 && (
                    <Bloque num="1" titulo="Alertas" icon={faExclamationTriangle} color={C_R}
                      badge={<span className="badge badge-danger">{alertas.length}</span>}>
                      {alertas.map((a, i) => <AlertaRow key={i} {...a} />)}
                    </Bloque>
                  )}

                  {/* ── 2: Resumen Ejecutivo jerarquizado ── */}
                  <Bloque num="2" titulo="Resumen Ejecutivo" icon={faChartLine} color="#007bff"
                    badge={<Pill color={sIns} size={12}>{totalConvM}/{totalMeta} inscritos</Pill>}>
                    {/* Fila 1 — 3 KPIs principales (grande) */}
                    <div className="row mb-3">
                      {[
                        { icon: faUserCheck, label: 'Inscritos mensuales', value: `${fmt(totalConvM)}/${fmt(totalMeta)}`, meta: `${totalMeta} ventas`, color: sIns, progress: Math.min(100, (totalConvM / Math.max(1, totalMeta)) * 100), accion: totalConvM >= totalMeta ? 'Mantener ritmo' : `Faltan ${totalMeta - totalConvM}`, large: true },
                        { icon: faChartLine, label: 'Conversión global',   value: `${pctConv}%`, meta: `${metas.pct_conversion || 10}%`, color: sConv, progress: Math.min(100, (pctConv / Math.max(1, metas.pct_conversion || 10)) * 100), accion: pctConv < 8 ? 'Revisar cierre' : 'Mantener', large: true },
                        { icon: faPhone,     label: 'Respuesta global',    value: `${pctResp}%`, meta: `${metas.pct_respuesta || 35}%`, color: sResp, progress: Math.min(100, (pctResp / Math.max(1, metas.pct_respuesta || 35)) * 100), accion: pctResp < 25 ? 'Revisar primer contacto' : 'OK', large: true },
                      ].map((x, i) => <div key={i} className="col-md-4 mb-2 mb-md-0"><KpiCard {...x} /></div>)}
                    </div>
                    {/* Fila 2 — 7 KPIs explicativos (pequeño) */}
                    <div className="row">
                      {[
                        { icon: faCalendarWeek,  label: 'Avance semanal',    value: `${avanceSem.inscritos_semana || 0}ins / ${avanceSem.reservas_semana || 0}res`, meta: `${metaInsSem}ins ó ${metaResSem}res`, color: semSem(+avanceSem.inscritos_semana || 0, +avanceSem.reservas_semana || 0, metaInsSem, metaResSem) },
                        { icon: faHourglassHalf, label: 'Maduración prom.',  value: madDias != null ? `${madDias}d` : 'S/D', meta: `≤${metas.maduracion_max_dias || 3}d`, color: semMad(madDias, metas.maduracion_max_dias || 3) },
                        { icon: faClock,         label: 'Contactabilidad',   value: contMin != null ? `${contMin}min` : 'S/D', meta: `≤${metas.contactabilidad_max || 10}min`, color: semCont(contMin, metas.contactabilidad_max || 10), accion: contPct != null ? `${contPct}% contactados en ≤${metas.contactabilidad_max || 10}min` : null },
                        { icon: faHeadset,       label: 'Conv. efectiva hoy',value: horasConv != null ? `${horasConv}h` : '0h', meta: `≥${metas.meta_horas_conv || 4}h/asesor`, color: semHef(hXAs ?? 0, metas.meta_horas_conv || 4), progress: hXAs != null ? Math.min(100, (hXAs / (metas.meta_horas_conv || 4)) * 100) : 0 },
                        { icon: faHandshake,     label: 'Tratos hoy',        value: fmt(prodHoy.tratos_hoy || 0), meta: `≥${metas.meta_tratos_dia || 3}`, color: semTrat(+prodHoy.tratos_hoy || 0, metas.meta_tratos_dia || 3), progress: Math.min(100, ((+prodHoy.tratos_hoy || 0) / Math.max(1, metas.meta_tratos_dia || 3)) * 100) },
                        { icon: faPhone,         label: 'Marcaciones hoy',   value: fmt(prodHoy.marcaciones_hoy || 0), meta: `≥${metas.meta_marcaciones_dia || 90}`, color: +prodHoy.marcaciones_hoy >= (metas.meta_marcaciones_dia || 90) ? C_V : +prodHoy.marcaciones_hoy >= (metas.meta_marcaciones_dia || 90) * .7 ? C_A : C_R, progress: Math.min(100, ((+prodHoy.marcaciones_hoy || 0) / Math.max(1, metas.meta_marcaciones_dia || 90)) * 100) },
                        { icon: faTasks,         label: 'Sin clasificar',    value: `${pctSinClas}%`, meta: `≤${metas.procesamiento_max_pct || 20}%`, color: semProc(pctSinClas, metas.procesamiento_max_pct || 20) },
                      ].map((x, i) => <div key={i} className="col-6 col-sm-4 col-xl mb-2 mb-xl-0"><KpiCard {...x} /></div>)}
                    </div>
                  </Bloque>

                  {/* ── 3: Ranking + Cierre mensual expandible ── */}
                  <Bloque num="3" titulo="Ranking de Asesores" icon={faUsers} color="#6f42c1">
                    <Ranking asesores={asesores} metas={metas} />
                    <CierreExpandible cierres={cierres} meses={meses} />
                  </Bloque>

                  {/* ── 4: Embudo + Pipeline (juntos) ── */}
                  <Bloque num="4" titulo="Embudo y Pipeline" icon={faBullhorn} color="#17a2b8">
                    <div className="row">
                      <div className="col-lg-6 mb-3 mb-lg-0">
                        <p className="mb-2" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#6c757d' }}>Embudo de campaña</p>
                        <Embudo funnel={funnel} metas={metas} />
                        <div className="row mt-3">
                          <div className="col-6 text-center">
                            <div style={{ fontWeight: 700, fontSize: 22, color: sResp }}>{metas.pct_respuesta || 35}%</div>
                            <small className="text-muted">Meta respuesta</small>
                          </div>
                          <div className="col-6 text-center">
                            <div style={{ fontWeight: 700, fontSize: 22, color: sConv }}>{metas.pct_conversion || 10}%</div>
                            <small className="text-muted">Meta conversión</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <p className="mb-2" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#6c757d' }}>Pipeline por fase</p>
                        <ChartTratos fases={tratosFase} />
                      </div>
                    </div>
                  </Bloque>

                  {/* ── 5: Análisis de Origen (tabs: Fuentes | Caliente vs Frío | Estados) ── */}
                  <Bloque num="5" titulo="Análisis de Origen" icon={faFilter} color="#28a745">
                    <OrigenTabs fuentes={fuentes} tiposLead={tiposLead} estados={procesam} />
                  </Bloque>

                  {/* ── 6: Evolución Histórica (subida) ── */}
                  <Bloque num="6" titulo="Evolución Histórica" icon={faChartLine} color="#007bff">
                    <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 8 }}>
                      <div className="btn-group btn-group-sm">
                        {[{ v: 'dia', l: 'Por día' }, { v: 'semana', l: 'Por semana' }, { v: 'mes', l: 'Por mes' }].map(o => (
                          <button key={o.v} onClick={() => setHistFiltros(f => ({ ...f, agrupacion: o.v }))}
                            className={`btn btn-${histFiltros.agrupacion === o.v ? 'primary' : 'outline-secondary'}`}
                            style={{ fontSize: 12 }}>
                            {o.l}
                          </button>
                        ))}
                      </div>
                      <div className="btn-group btn-group-sm">
                        {[{ v: '7d', l: '7d' }, { v: '30d', l: '30d' }, { v: '60d', l: '60d' }, { v: '90d', l: '90d' }, { v: 'todo', l: 'Todo' }].map(o => (
                          <button key={o.v} onClick={() => setHistFiltros(f => ({ ...f, preset: o.v }))}
                            className={`btn btn-${histFiltros.preset === o.v ? 'primary' : 'outline-secondary'}`}
                            style={{ fontSize: 12 }}>
                            {o.l}
                          </button>
                        ))}
                      </div>
                      <select value={histFiltros.asesor} onChange={e => { const v = +e.target.value; setHistFiltros(f => ({ ...f, asesor: v, preset: v > 0 ? 'todo' : f.preset })) }}
                        className="form-control form-control-sm" style={{ width: 'auto' }}>
                        <option value={0}>Todos los asesores</option>
                        {asesores.map(a => <option key={a.id_usuario} value={a.id_usuario}>{a.nombre?.split(' ').filter(Boolean).slice(0, 3).join(' ')}</option>)}
                      </select>
                      <select value={histFiltros.fuente} onChange={e => { const v = +e.target.value; setHistFiltros(f => ({ ...f, fuente: v, preset: v > 0 ? 'todo' : f.preset })) }}
                        className="form-control form-control-sm" style={{ width: 'auto' }}>
                        <option value={0}>Todas las fuentes</option>
                        {fuentes.map(f => <option key={f.id_fuente} value={f.id_fuente}>{f.nombre_fuente}</option>)}
                      </select>
                      <select value={histFiltros.tipo_lead} onChange={e => { const v = e.target.value; setHistFiltros(f => ({ ...f, tipo_lead: v, preset: v ? 'todo' : f.preset })) }}
                        className="form-control form-control-sm" style={{ width: 'auto' }}>
                        <option value="">Todos</option>
                        <option value="caliente">Solo caliente</option>
                        <option value="frio">Solo frío</option>
                      </select>
                      <select value={histFiltros.estado_lead} onChange={e => { const v = e.target.value; setHistFiltros(f => ({ ...f, estado_lead: v, preset: v ? 'todo' : f.preset })) }}
                        className="form-control form-control-sm" style={{ width: 'auto' }}>
                        <option value="">Todos</option>
                        <option value="respondio">Respondió</option>
                        <option value="no_respondio">No respondió</option>
                      </select>
                    </div>
                    <div style={{ minHeight: 220, position: 'relative' }}>
                      {histLoading
                        ? <div style={{ height: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa', borderRadius: 6 }}>
                            <div className="spinner-border text-primary" style={{ width: 28, height: 28 }} />
                            <p className="text-muted mt-2 mb-0" style={{ fontSize: 12 }}>Cargando…</p>
                          </div>
                        : histError
                          ? <div style={{ height: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fff3cd', borderRadius: 6, border: '1px solid #ffc107', padding: 16 }}>
                              <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: 28, color: '#856404', marginBottom: 8 }} />
                              <p className="mb-1 font-weight-bold" style={{ fontSize: 13, color: '#856404' }}>Error al cargar el gráfico</p>
                              <p className="mb-0 text-center" style={{ fontSize: 11, color: '#856404', wordBreak: 'break-all' }}>{histError}</p>
                            </div>
                          : <ChartHistorico data={histData} agrupacion={histFiltros.agrupacion} />
                      }
                    </div>
                  </Bloque>

                  {/* ── 7: Productividad de Hoy (colapsado por defecto) ── */}
                  <Bloque num="7" titulo="Productividad de Hoy" icon={faChartLine} color="#28a745" defaultOpen={false}
                    badge={<small className="text-muted">{new Date().toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long' })}</small>}>
                    <div className="row mb-3">
                      {[
                        { l: 'Tratos',      icon: faHandshake, v: prodHoy.tratos_hoy     || 0, m: metas.meta_tratos_dia    || 3,  col: semTrat(+prodHoy.tratos_hoy || 0, metas.meta_tratos_dia || 3) },
                        { l: 'Marcaciones', icon: faPhone,     v: prodHoy.marcaciones_hoy || 0, m: metas.meta_marcaciones_dia||90, col: +prodHoy.marcaciones_hoy >= (metas.meta_marcaciones_dia||90) ? C_V : +prodHoy.marcaciones_hoy >= (metas.meta_marcaciones_dia||90)*.7 ? C_A : C_R },
                        { l: 'Inscritos',   icon: faUserCheck, v: prodHoy.inscritos_hoy   || 0, m: null,                           col: +(prodHoy.inscritos_hoy || 0) >= 1 ? C_V : C_A },
                        { l: 'Reservas',    icon: faBookmark,  v: prodHoy.reservas_hoy    || 0, m: null,                           col: null },
                      ].map(x => (
                        <div key={x.l} className="col-6 col-sm-3 mb-2">
                          <div className="box p-3 text-center" style={{ borderTop: `4px solid ${x.col || '#dee2e6'}` }}>
                            <FontAwesomeIcon icon={x.icon} style={{ color: x.col || '#aaa', fontSize: 22, display: 'block', margin: '0 auto 8px' }} />
                            <h3 style={{ fontWeight: 800, fontSize: 28, color: x.col || '#212529', marginBottom: 2, lineHeight: 1 }}>{fmt(x.v)}</h3>
                            <small className="text-muted d-block" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>{x.l}</small>
                            {x.m != null && <small className="text-muted">meta: {x.m}</small>}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-3" style={{ background: '#f8f9fa', borderRadius: 4 }}>
                      <p className="mb-3 d-flex justify-content-between align-items-center" style={{ fontSize: 13, fontWeight: 600 }}>
                        <span><FontAwesomeIcon icon={faHeadset} className="mr-2 text-primary" />Conversación efectiva por asesor</span>
                        <small className="text-muted font-weight-normal">Meta: {metas.meta_horas_conv || 4}h/asesor</small>
                      </p>
                      <ConvEf asesores={convEf.asesores} meta={metas.meta_horas_conv || 4} />
                    </div>
                  </Bloque>

                </>)}

                {!loadingProd && !prodData && pidSel && (
                  <div className="box p-5 text-center">
                    <FontAwesomeIcon icon={faChartLine} style={{ fontSize: 40, color: '#dee2e6', display: 'block', margin: '0 auto 12px' }} />
                    <p className="text-muted mb-1">Sin datos para este programa</p>
                    <small className="text-muted">Verifica que la campaña esté activa y tenga leads registrados</small>
                  </div>
                )}
                </ErrorBoundary>
              </SpinnerLoading>
            </>)}
          </SpinnerLoading>
        </div>
      </div>

      {modalMetas && (
        <ModalMetas
          idCamp={prodData?.id_camp || 0}
          metasActuales={metas}
          asesores={asesores}
          fuentes={fuentes}
          onClose={() => setModalMetas(false)}
          onSaved={() => pidSel && cargarProducto(pidSel)}
        />
      )}
    </Layout>
  )
}

export default ReporteKpisVentas
